// The business timezone from its address (2026-10-24). The timezone used to
// come only from the owner's device at first login, so an account created on
// a browser running in UTC (a test machine, a cloud browser) planned the whole
// day in UTC: in Portland, "today" flipped to tomorrow at 5pm. A US address
// now decides the zone; the device is only a fallback, and UTC never sticks.
// States that span two zones use the zone most of the state lives in; the
// owner can still pick the exact one in Settings > Business.

const STATE_TZ = {
  AL: 'America/Chicago',    AK: 'America/Anchorage',  AZ: 'America/Phoenix',    AR: 'America/Chicago',
  CA: 'America/Los_Angeles', CO: 'America/Denver',    CT: 'America/New_York',   DE: 'America/New_York',
  DC: 'America/New_York',   FL: 'America/New_York',   GA: 'America/New_York',   HI: 'Pacific/Honolulu',
  ID: 'America/Boise',      IL: 'America/Chicago',    IN: 'America/Indiana/Indianapolis', IA: 'America/Chicago',
  KS: 'America/Chicago',    KY: 'America/New_York',   LA: 'America/Chicago',    ME: 'America/New_York',
  MD: 'America/New_York',   MA: 'America/New_York',   MI: 'America/Detroit',    MN: 'America/Chicago',
  MS: 'America/Chicago',    MO: 'America/Chicago',    MT: 'America/Denver',     NE: 'America/Chicago',
  NV: 'America/Los_Angeles', NH: 'America/New_York',  NJ: 'America/New_York',   NM: 'America/Denver',
  NY: 'America/New_York',   NC: 'America/New_York',   ND: 'America/Chicago',    OH: 'America/New_York',
  OK: 'America/Chicago',    OR: 'America/Los_Angeles', PA: 'America/New_York',  RI: 'America/New_York',
  SC: 'America/New_York',   SD: 'America/Chicago',    TN: 'America/Chicago',    TX: 'America/Chicago',
  UT: 'America/Denver',     VT: 'America/New_York',   VA: 'America/New_York',   WA: 'America/Los_Angeles',
  WV: 'America/New_York',   WI: 'America/Chicago',    WY: 'America/Denver',     PR: 'America/Puerto_Rico',
  GU: 'Pacific/Guam',       VI: 'America/St_Thomas',  AS: 'Pacific/Pago_Pago',  MP: 'Pacific/Saipan',
};

const NAME_TO_CODE = {
  alabama: 'AL', alaska: 'AK', arizona: 'AZ', arkansas: 'AR', california: 'CA', colorado: 'CO',
  connecticut: 'CT', delaware: 'DE', 'district of columbia': 'DC', florida: 'FL', georgia: 'GA',
  hawaii: 'HI', idaho: 'ID', illinois: 'IL', indiana: 'IN', iowa: 'IA', kansas: 'KS', kentucky: 'KY',
  louisiana: 'LA', maine: 'ME', maryland: 'MD', massachusetts: 'MA', michigan: 'MI', minnesota: 'MN',
  mississippi: 'MS', missouri: 'MO', montana: 'MT', nebraska: 'NE', nevada: 'NV', 'new hampshire': 'NH',
  'new jersey': 'NJ', 'new mexico': 'NM', 'new york': 'NY', 'north carolina': 'NC', 'north dakota': 'ND',
  ohio: 'OH', oklahoma: 'OK', oregon: 'OR', pennsylvania: 'PA', 'rhode island': 'RI',
  'south carolina': 'SC', 'south dakota': 'SD', tennessee: 'TN', texas: 'TX', utah: 'UT', vermont: 'VT',
  virginia: 'VA', washington: 'WA', 'west virginia': 'WV', wisconsin: 'WI', wyoming: 'WY',
  'puerto rico': 'PR', guam: 'GU',
};

// "UTC", "Etc/UTC", "GMT"...: a device default, never a real cafe's zone.
export const isUtcZone = (tz) => /^(etc\/)?(utc|gmt|uct|zulu|universal)(\+0|-0|0)?$/i.test(String(tz || ''));

// IANA zone for a US address (state code or name), or null.
export function timezoneForAddress(country, province) {
  const c = String(country || '').trim().toUpperCase();
  if (c && c !== 'US' && c !== 'USA' && c !== 'UNITED STATES') return null;
  const p = String(province || '').trim();
  if (!p) return null;
  const code = p.length === 2 ? p.toUpperCase() : NAME_TO_CODE[p.toLowerCase()];
  return STATE_TZ[code] ?? null;
}

// Minutes from UTC for a zone right now (-420 for Los Angeles in summer), or null.
const offsetMinutes = (tz, at = new Date()) => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric',
    }).formatToParts(at);
    const v = Object.fromEntries(parts.map((p) => [p.type, Number(p.value)]));
    return Math.round((Date.UTC(v.year, v.month - 1, v.day, v.hour, v.minute) - at.getTime()) / 60000);
  } catch {
    return null;
  }
};

// A saved zone fits the address when it's within an hour of the address's zone (2026-10-25).
// The hour leaves room for states that span two zones (western Florida, eastern Oregon), where
// the owner may have picked the other one; anything further off came from a device somewhere
// else (a Manila laptop, a UTC test browser) and gives way to the address.
export function zoneFitsAddress(savedTz, addressTz) {
  if (!savedTz || !addressTz || savedTz === addressTz) return true;
  if (isUtcZone(savedTz)) return false;
  const a = offsetMinutes(savedTz);
  const b = offsetMinutes(addressTz);
  if (a == null) return false; // not a real zone name
  return b == null || Math.abs(a - b) <= 60;
}
